import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";

import { Box } from "@mui/material";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import CloseIcon from "@mui/icons-material/Close";
import Select from "react-select";

import LogoAmex from "../assets/img/logo_amex.png";
import LogoPayu from "../assets/img/logo_payu.png";

import "../assets/css/CheckoutPage.css";

import { CartContext } from "../context/CartContext";
import { formatNumber } from "../utils/formatNumber";

// Services
import { getDepartments, getMunicipalities } from "../services/locationService";
import { createOrder } from "../services/orderService";

const CheckoutPage = () => {
    const navigate = useNavigate();
    const [showModal, setShowModal] = useState(false);
    const [departments, setDepartments] = useState([]);
    const [municipalities, setMunicipalities] = useState([]);
    const [selectedDepartment, setSelectedDepartment] = useState(null);
    const [selectedMunicipality, setSelectedMunicipality] = useState(null);
    const [selectedAddress, setSelectedAddress] = useState(null);
    const [paymentMethod, setPaymentMethod] = useState(null);
    const [formData, setFormData] = useState({
        documentId: "",
        fullName: "",
        country: "Colombia",
        street: "",
        address: "",
        city: "",
        numberPhone: "",
        addressDefault: false,
        departmentId: null,
        municipality: null,
    });

    const { cartItems } = useContext(CartContext);

    const totalPrice = cartItems.reduce((total, item) => total + item.quantity * item.price, 0);

    const groupedItems: Record<string, typeof cartItems> = cartItems.reduce((acc, item) => {
        const seller = item.seller;
        if (!acc[seller]) {
            acc[seller] = [];
        }
        acc[seller].push(item);
        return acc;
    }, {});

    const calculateShipping = () => {
        return Object.entries(groupedItems).map(([_, items]) => {
            return items.reduce((sum, item) => {
                const isLocalShipping = item.locations?.some(
                    (location) =>
                        selectedDepartment?.label === location.department &&
                        selectedMunicipality?.label === location.municipality
                );

                return sum + (isLocalShipping ? item.priceShipping : item.priceShippingNational);
            }, 0);
        });
    };

    const totalPriceShipping = calculateShipping();
    const grandTotalShipping = totalPriceShipping.reduce((sum, price) => sum + price, 0);

    const handleModalToggle = () => {
        setShowModal(!showModal);
    };

    const handlePaymentMethodChange = (e) => {
        setPaymentMethod(e.target.id);
    };

    const handleSelectChangeDepartment = async (selectedOption) => {
        setSelectedDepartment(selectedOption);
        setSelectedMunicipality(null);

        try {
            const data = await getMunicipalities(selectedOption.value);
            const formattedData = data.map((item) => ({
                value: item.id,
                label: item.name,
            }));
            setMunicipalities(formattedData);
        } catch (error) {
            console.error("Error fetching municipalities:", error);
        }
    };

    const handleSelectChangeMunicipality = (selectedOption) => {
        setSelectedMunicipality(selectedOption);
        setFormData({
            ...formData,
            municipality: selectedOption?.label || "",
        });
    };

    const fetchDepartments = async () => {
        try {
            const data = await getDepartments();
            const formattedData = data.map((item) => ({
                value: item.id,
                label: item.name,
            }));
            setDepartments(formattedData);
        } catch (error) {
            console.error("Error loading departments:", error);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === "checkbox" ? checked : value,
        });
    };

    const handleAddAddress = async () => {
        try {
            setSelectedAddress(formData);
            console.log("Dirección agregada exitosamente");
            setShowModal(false);
        } catch (error) {
            alert("Error al agregar la dirección");
        }
    };

    const handleCreateOrder = async () => {
        if (!selectedAddress || !paymentMethod) {
            alert("Por favor, completa la dirección y selecciona un método de pago.");
            return;
        }

        const orderData = {
            address: {
                documentId: selectedAddress.documentId,
                fullName: selectedAddress.fullName,
                country: selectedAddress.country,
                street: selectedAddress.street,
                address: selectedAddress.address,
                city: selectedAddress.city,
                numberPhone: selectedAddress.numberPhone,
                addressDefault: selectedAddress.addressDefault,
                departmentId: selectedDepartment.value,
                municipality: selectedMunicipality.value,
            },
            paymentMethod: paymentMethod,
            subtotal: totalPrice,
            totalShipping: grandTotalShipping,
            totalAmount: totalPrice + grandTotalShipping,
            details: cartItems,
        };

        try {
            const response = await createOrder(orderData);
            console.log("Pedido creado exitosamente");
            if (response) {
                localStorage.removeItem("cart");
                navigate("/order-confirmation");
            }
        } catch (error) {
            alert("Error al crear el pedido");
        }
    };

    const cartFromLocalStorage = localStorage.getItem("cart") ? true : false;

    useEffect(() => {
        fetchDepartments();
    }, []);

    return (
        <Box className="body-product-page body-checkout-page w-100 py-5">
            <div className="checkout-page container">
                {!cartFromLocalStorage ? (
                    <div className="text-center">
                        <h2 className="mb-4">Tu carrito está vacío</h2>
                        <button className="buy-button" onClick={() => navigate("/")}>
                            Volver al inicio
                        </button>
                    </div>
                ):(
                    <div className="row">
                        {/* Título */}
                        <div className="col-12 mb-4 d-md-block d-none">
                            <h1 className="title-checkout">Checkout</h1>
                        </div>

                        {/* Dirección de entrega */}
                        <div className="col-12 col-lg-7 pe-md-5">
                            <div id="addresses" className="w-100 d-flex justify-content-between align-items-start py-5 px-4 border-bottom border-top">
                                {selectedAddress ? (
                                    <div>
                                        <h5 className="text-body-secondary fw-bold">Dirección de entrega</h5>
                                        <p className="mb-1">
                                            <strong>{selectedAddress.fullName}</strong>
                                        </p>
                                        <p className="mb-1">{selectedAddress.street}</p>
                                        <p className="mb-1">{selectedAddress.address}</p>
                                        <p className="mb-0">
                                            {selectedAddress.city}, {selectedDepartment?.label} - {selectedMunicipality?.label}, {selectedAddress.country}.
                                        </p>
                                    </div>
                                ) : (
                                    <h5 className="text-body-secondary fw-bold">Dirección de entrega</h5>
                                )}

                                <button className="buy-button" onClick={handleModalToggle}>
                                    {selectedAddress ? "Cambiar dirección" : "+ Agregar dirección"}
                                </button>
                            </div>

                            {/* Modal */}
                            {showModal && (
                                <div className="modal-overlay modal-checkout">
                                    <div className="modal-content">
                                        <div className="modal-header px-md-5 px-4 py-4 border-bottom d-flex justify-content-between align-items-center">
                                            <h2>Introduce una dirección de envío</h2>
                                            <CloseIcon className="close-modal" onClick={handleModalToggle} />
                                        </div>
                                        <form className="px-md-5 px-4 py-4">
                                            <input
                                                name="documentId"
                                                type="text"
                                                placeholder="Identificación"
                                                value={formData.documentId}
                                                onChange={handleInputChange}
                                            />
                                            <input
                                                name="fullName"
                                                type="text"
                                                placeholder="Nombre completo"
                                                value={formData.fullName}
                                                onChange={handleInputChange}
                                            />
                                            <input type="hidden" name="country" value="Colombia" />
                                            <h2 className="mt-4">Dirección</h2>
                                            <input
                                                type="text"
                                                placeholder="Calle y número"
                                                name="street"
                                                value={formData.street}
                                                onChange={handleInputChange}
                                            />
                                            <input
                                                type="text"
                                                placeholder="Apartamento, habitación, etc. (Opcional)"
                                                name="address"
                                                value={formData.address}
                                                onChange={handleInputChange}
                                            />
                                            <Select
                                                options={departments}
                                                placeholder="Departamento"
                                                onChange={handleSelectChangeDepartment}
                                                value={selectedDepartment}
                                                className="mb-3"
                                                name="department"
                                            />
                                            <Select
                                                options={municipalities}
                                                placeholder="Municipio"
                                                onChange={handleSelectChangeMunicipality}
                                                value={selectedMunicipality}
                                                className="mb-3"
                                                name="municipality"
                                            />
                                            <input
                                                type="text"
                                                placeholder="Ciudad / Localidad"
                                                name="city"
                                                value={formData.city}
                                                onChange={handleInputChange}
                                            />
                                            <input
                                                type="text"
                                                placeholder="Teléfono"
                                                name="numberPhone"
                                                value={formData.numberPhone}
                                                onChange={handleInputChange}
                                            />
                                            {/* <div className="checkbox-container d-flex gap-2 align-items-center justify-content-start mt-3">
                                                <input
                                                    type="checkbox"
                                                    id="defaultAddress"
                                                    name="addressDefault"
                                                    checked={formData.addressDefault}
                                                    onChange={handleInputChange}
                                                />
                                                <label htmlFor="defaultAddress">Guardar como predeterminada</label>
                                            </div> */}
                                            <div className="d-flex justify-content-center align-items-center mt-4">
                                                <button
                                                    type="button"
                                                    id="add-address"
                                                    className="buy-button"
                                                    onClick={handleAddAddress}
                                                >
                                                    Continuar
                                                </button>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            )}

                            {/* Métodos de pago */}
                            <div className="mt-5">
                                <h5 className="text-body-secondary fw-bold mb-5">Selecciona un método de pago</h5>
                                <div className="payment-methods-checkout mt-3">
                                    <div className="form-check">
                                        <label className="form-check-label" htmlFor="pse">
                                            <img src={LogoAmex} alt="PSE" /> PSE
                                        </label>
                                        <input
                                            onChange={handlePaymentMethodChange}
                                            className="form-check-input"
                                            type="radio"
                                            name="paymentMethod"
                                            id="pse"
                                        />
                                    </div>
                                    <div className="form-check">
                                        <label className="form-check-label" htmlFor="creditCard">
                                            <CreditCardIcon /> TARJETA DÉBITO O CRÉDITO
                                        </label>
                                        <input
                                            onChange={handlePaymentMethodChange}
                                            className="form-check-input"
                                            type="radio"
                                            name="paymentMethod"
                                            id="creditCard"
                                        />
                                    </div>
                                    <div className="form-check">
                                        <label className="form-check-label" htmlFor="bancolombia">
                                            <img src={LogoPayu} alt="Bancolombia" /> TRANSFERENCIA BANCOLOMBIA
                                        </label>
                                        <input
                                            onChange={handlePaymentMethodChange}
                                            className="form-check-input"
                                            type="radio"
                                            name="paymentMethod"
                                            id="bancolombia"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Resumen del carrito */}
                        <div className="col-12 col-lg-5 d-flex justify-content-end align-items-start">
                            <div className="cart-summary p-md-5 p-4">
                                <h3 className="mb-5">Resumen del carrito</h3>
                                <ul className="list-unstyled d-flex flex-column gap-2 mt-3">
                                    <li className="d-flex justify-content-between">
                                        <span>Subtotal:</span>
                                        <span>${formatNumber(totalPrice)}</span>
                                    </li>
                                    {totalPriceShipping.map((totalShipping, index) => (
                                        <li className="d-flex justify-content-between" key={index}>
                                            <span>Envío paquete {index + 1}:</span>
                                            <span>$ {formatNumber(totalShipping)}</span>
                                        </li>
                                    ))}
                                    <li className="d-flex justify-content-between border-top pt-3">
                                        <strong>Total con envío:</strong>
                                        <strong>${formatNumber(totalPrice + grandTotalShipping)}</strong>
                                    </li>
                                </ul>

                                <div className="w-100 d-flex justify-content-center my-5">
                                    <button onClick={handleCreateOrder} className="buy-button w-100">Pagar pedido</button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Box>
    );
};

export default CheckoutPage;